import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { VersionInfo } from '../../components/Layout/VersionInfo';
import InstallAppBanner from '../../components/InstallAppBanner';
import PasswordInput from '../../components/common/PasswordInput';
import axios from 'axios';
import { universalLogin } from '../../services/authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface RegistrationForm {
  business_name: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  customer_type: string;
  referral_source: string;
}

interface EmailCheckResult {
  exists: boolean;
  user_exists: boolean;
  pending_exists: boolean;
  account_type?: string;
  message?: string;
}

const PortalLogin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signin');

  // Sign In state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Sign Up state
  const [registrationForm, setRegistrationForm] = useState<RegistrationForm>({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    customer_type: 'for_profit_farm',
    referral_source: '',
  });
  const [registrationError, setRegistrationError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);

  // Email check state
  const [emailCheckResult, setEmailCheckResult] = useState<EmailCheckResult | null>(null);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');

  // Multi-account selector state
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState<any[]>([]);
  const [savedCredentials, setSavedCredentials] = useState<{ email: string; password: string } | null>(null);

  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading } = usePortalAuth();
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading) {
      if (isAuthenticated) {
        console.log('[PortalLogin] Already authenticated, redirecting to dashboard');
        window.location.href = '/portal/dashboard';
      } else {
        setCheckingAuth(false);
      }
    }

    // Failsafe timeout
    const timeout = setTimeout(() => {
      setCheckingAuth(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [authLoading, isAuthenticated]);

  // Debounced email check
  const checkEmail = useCallback(async (emailToCheck: string) => {
    if (!emailToCheck || !emailToCheck.includes('@')) {
      setEmailCheckResult(null);
      return;
    }

    setEmailCheckLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/portal/register/check_email/`, {
        params: { email: emailToCheck }
      });
      setEmailCheckResult(response.data);
    } catch (err) {
      setEmailCheckResult(null);
    } finally {
      setEmailCheckLoading(false);
    }
  }, []);

  // Debounce email check
  useEffect(() => {
    if (activeTab !== 'signup') return;

    const timer = setTimeout(() => {
      checkEmail(registrationForm.email);
    }, 500);

    return () => clearTimeout(timer);
  }, [registrationForm.email, activeTab, checkEmail]);

  // Handle forgot password submission
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotPasswordError('');
    setForgotPasswordLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/portal/password/forgot_password/`, {
        email: forgotPasswordEmail
      });
      setForgotPasswordSuccess(true);
    } catch (err: any) {
      setForgotPasswordError(
        err.response?.data?.error || 'Failed to send reset email. Please try again.'
      );
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use universal login - works for any user type
      const result = await universalLogin(email, password);

      if (result.success) {
        // Check if multiple accounts found
        if (result.auto_login === false && result.accounts && result.accounts.length > 1) {
          // Show account selector
          setAvailableAccounts(result.accounts);
          setSavedCredentials({ email, password });
          setShowAccountSelector(true);
          setLoading(false);
          return;
        }

        // Single account or auto-login - redirect to the appropriate portal
        if (result.redirect_url) {
          window.location.href = result.redirect_url;
        } else {
          // Fallback to portal dashboard
          window.location.href = '/portal/dashboard';
        }
      } else if (result.requires_verification) {
        setError('Please verify your email before logging in');
      } else {
        setError(result.error || 'Invalid email or password');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelect = async (account: any) => {
    if (!savedCredentials) return;

    setLoading(true);
    setError('');

    try {
      // Call universal login with the selected account type and portal_user_id
      const body: any = {
        email: savedCredentials.email.toLowerCase().trim(),
        password: savedCredentials.password,
        account_type: account.type,
      };

      // For portal accounts, include portal_user_id to identify which one
      if (account.portal_user_id) {
        body.portal_user_id = account.portal_user_id;
      }

      const response = await fetch(`${API_BASE_URL}/auth/universal-login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store user data based on account type
        if (data.user_type === 'portal') {
          localStorage.setItem('portal_user', JSON.stringify(data.user));
        } else if (data.user_type === 'bridge_club') {
          localStorage.setItem('bridge_club_member', JSON.stringify(data.member));
        }

        // Redirect
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        } else if (account.type === 'bridge_club') {
          window.location.href = '/bridge-club/dashboard';
        } else {
          window.location.href = '/portal/dashboard';
        }
      } else {
        setError(data.error || 'Login failed. Please try again.');
        setShowAccountSelector(false);
      }
    } catch (err: any) {
      setError('Network error. Please try again.');
      setShowAccountSelector(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setRegistrationForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegistrationError('');

    // Validate email doesn't already exist
    if (emailCheckResult?.exists) {
      setRegistrationError('An account with this email already exists. Please use a different email or sign in.');
      return;
    }

    setRegistrationLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/portal/register/`, {
        business_name: registrationForm.business_name,
        contact_name: registrationForm.contact_name,
        email: registrationForm.email,
        phone: registrationForm.phone,
        address: registrationForm.address,
        city: registrationForm.city,
        state: registrationForm.state,
        zip_code: registrationForm.zip_code,
        customer_type: registrationForm.customer_type,
        referral_source: registrationForm.referral_source,
      });

      // Handle resend case
      if (response.data.message?.includes('resent')) {
        setRegistrationError('');
      }

      setRegistrationSuccess(true);
    } catch (err: any) {
      setRegistrationError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Registration failed. Please try again.'
      );
    } finally {
      setRegistrationLoading(false);
    }
  };

  if (registrationSuccess) {
    return (
      <div style={styles.container}>
        <div style={styles.loginCard}>
          <div style={styles.header}>
            <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." style={styles.logo} />
            <h1 style={styles.title}>Wholesale Customer Portal</h1>
            <p style={styles.subtitle}>Red Bridge Farm Feed Co.</p>
          </div>

          <div style={styles.successMessage}>
            <div style={styles.successIcon}>✉</div>
            <h2 style={styles.successTitle}>Check Your Email!</h2>
            <p style={styles.successText}>
              We've sent a verification email to{' '}
              <strong>{registrationForm.email}</strong>.
            </p>
            <p style={styles.successSubtext}>
              Click the link in the email to verify your address and set your password.
              After that, your registration will be reviewed by our team.
            </p>
            <button
              style={styles.backButton}
              onClick={() => {
                setRegistrationSuccess(false);
                setActiveTab('signin');
                setRegistrationForm({
                  business_name: '',
                  contact_name: '',
                  email: '',
                  phone: '',
                  address: '',
                  city: '',
                  state: '',
                  zip_code: '',
                  customer_type: 'for_profit_farm',
                  referral_source: '',
                });
              }}
            >
              Back to Sign In
            </button>
          </div>

          <div style={styles.footer}>
            <p style={styles.motto}>Healthy Feed. Healthy Food. Healthy Families.</p>
            <VersionInfo />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{ ...styles.loginCard, maxWidth: activeTab === 'signup' ? '520px' : '420px' }}>
        <div style={styles.header}>
          <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." style={styles.logo} />
          <h1 style={styles.title}>Wholesale Customer Portal</h1>
          <p style={styles.subtitle}>Red Bridge Farm Feed Co.</p>
          <p style={styles.tagline}>NON-GMO, SOY & CORN-FREE FEEDS</p>
        </div>

        {/* Tab Toggle */}
        <div style={styles.tabContainer}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'signin' ? styles.activeTab : {}),
            }}
            onClick={() => setActiveTab('signin')}
          >
            Sign In
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'signup' ? styles.activeTab : {}),
            }}
            onClick={() => setActiveTab('signup')}
          >
            Create Account
          </button>
        </div>

        {activeTab === 'signin' ? (
          <>
            <form onSubmit={handleSignIn} style={styles.form}>
              {error && <div style={styles.error}>{error}</div>}

              <div style={styles.inputGroup}>
                <label style={styles.label}>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={styles.input}
                  placeholder="your.email@example.com"
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Password</label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={styles.input}
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>

              <button type="submit" style={styles.submitButton} disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In to Customer Portal'}
              </button>
            </form>

            <div style={styles.forgotPassword}>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(true);
                  setForgotPasswordEmail(email);
                  setForgotPasswordError('');
                  setForgotPasswordSuccess(false);
                }}
                style={styles.forgotPasswordLink}
              >
                Forgot your password?
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSignUp} style={styles.form}>
            {registrationError && <div style={styles.error}>{registrationError}</div>}

            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Business Name *</label>
                <input
                  type="text"
                  name="business_name"
                  value={registrationForm.business_name}
                  onChange={handleRegistrationChange}
                  style={styles.input}
                  placeholder="Your Farm or Business Name"
                  required
                  disabled={registrationLoading}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Contact Name *</label>
                <input
                  type="text"
                  name="contact_name"
                  value={registrationForm.contact_name}
                  onChange={handleRegistrationChange}
                  style={styles.input}
                  placeholder="Your Full Name"
                  required
                  disabled={registrationLoading}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={registrationForm.email}
                  onChange={handleRegistrationChange}
                  style={{
                    ...styles.input,
                    ...(emailCheckResult?.exists ? styles.inputError : {}),
                    ...(registrationForm.email && !emailCheckResult?.exists && !emailCheckLoading ? styles.inputValid : {})
                  }}
                  placeholder="your.email@example.com"
                  required
                  disabled={registrationLoading}
                  autoComplete="email"
                />
                {emailCheckLoading && (
                  <span style={styles.emailCheckLoading}>Checking...</span>
                )}
                {emailCheckResult?.exists && (
                  <span style={styles.emailCheckError}>
                    {emailCheckResult.pending_exists
                      ? 'This email already has a pending registration. Please wait for approval.'
                      : emailCheckResult.account_type === 'staff'
                      ? 'This email belongs to a staff account. Please use a different email.'
                      : 'An account with this email already exists. Please sign in instead.'}
                  </span>
                )}
                {registrationForm.email && !emailCheckResult?.exists && !emailCheckLoading && registrationForm.email.includes('@') && (
                  <span style={styles.emailCheckValid}>Email available</span>
                )}
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={registrationForm.phone}
                  onChange={handleRegistrationChange}
                  style={styles.input}
                  placeholder="(509) 555-1234"
                  required
                  disabled={registrationLoading}
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Address</label>
              <input
                type="text"
                name="address"
                value={registrationForm.address}
                onChange={handleRegistrationChange}
                style={styles.input}
                placeholder="123 Farm Road"
                disabled={registrationLoading}
              />
            </div>

            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>City</label>
                <input
                  type="text"
                  name="city"
                  value={registrationForm.city}
                  onChange={handleRegistrationChange}
                  style={styles.input}
                  placeholder="Spokane"
                  disabled={registrationLoading}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>State</label>
                <input
                  type="text"
                  name="state"
                  value={registrationForm.state}
                  onChange={handleRegistrationChange}
                  style={styles.input}
                  placeholder="WA"
                  maxLength={2}
                  disabled={registrationLoading}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Zip</label>
                <input
                  type="text"
                  name="zip_code"
                  value={registrationForm.zip_code}
                  onChange={handleRegistrationChange}
                  style={styles.input}
                  placeholder="99201"
                  maxLength={10}
                  disabled={registrationLoading}
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Customer Type *</label>
              <select
                name="customer_type"
                value={registrationForm.customer_type}
                onChange={handleRegistrationChange}
                style={styles.select}
                required
                disabled={registrationLoading}
              >
                <option value="for_profit_farm">For-Profit Farm</option>
                <option value="non_profit_farm">Non-Profit Farm</option>
                <option value="retail_supplier">Retail Supplier</option>
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>How did you hear about us?</label>
              <input
                type="text"
                name="referral_source"
                value={registrationForm.referral_source}
                onChange={handleRegistrationChange}
                style={styles.input}
                placeholder="Friend, Google, Facebook, etc."
                disabled={registrationLoading}
              />
            </div>

            <button type="submit" style={styles.submitButton} disabled={registrationLoading}>
              {registrationLoading ? 'Submitting...' : 'Create Account'}
            </button>

            <p style={styles.termsText}>
              By creating an account, you agree to our terms of service and privacy policy.
            </p>
          </form>
        )}

        <div style={styles.footer}>
          <p style={styles.motto}>Healthy Feed. Healthy Food. Healthy Families.</p>
          <div style={styles.contactInfo}>
            <p style={styles.contactText}>
              Questions? Contact us at{' '}
              <a href="tel:509-738-9004" style={styles.contactLink}>509-738-9004</a>
              {' or '}
              <a href="mailto:office@theredbridgefarm.com" style={styles.contactLink}>
                office@theredbridgefarm.com
              </a>
            </p>
          </div>

          <div style={styles.bridgeClubSwitch}>
            Bridge Club Member?{' '}
            <a
              href="https://bridgeclub.redbridgefeed.com/bridge-club/login"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.bridgeClubLink}
            >
              Bridge Club →
            </a>
          </div>

          <VersionInfo />
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div style={styles.modalOverlay} onClick={() => setShowForgotPassword(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button
              style={styles.modalClose}
              onClick={() => setShowForgotPassword(false)}
              aria-label="Close"
            >
              &times;
            </button>

            {forgotPasswordSuccess ? (
              <div style={styles.modalSuccess}>
                <div style={styles.successIcon}>✓</div>
                <h2 style={styles.modalTitle}>Check Your Email</h2>
                <p style={styles.modalText}>
                  If an account exists with <strong>{forgotPasswordEmail}</strong>, we've sent password reset instructions to that address.
                </p>
                <p style={styles.modalSubtext}>
                  The link will expire in 24 hours. Check your spam folder if you don't see it.
                </p>
                <button
                  style={styles.modalButton}
                  onClick={() => setShowForgotPassword(false)}
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <>
                <h2 style={styles.modalTitle}>Reset Your Password</h2>
                <p style={styles.modalText}>
                  Enter your email address and we'll send you instructions to reset your password.
                </p>

                <form onSubmit={handleForgotPassword}>
                  {forgotPasswordError && (
                    <div style={styles.error}>{forgotPasswordError}</div>
                  )}

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Email Address</label>
                    <input
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      style={styles.input}
                      placeholder="your.email@example.com"
                      required
                      disabled={forgotPasswordLoading}
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    style={styles.modalButton}
                    disabled={forgotPasswordLoading}
                  >
                    {forgotPasswordLoading ? 'Sending...' : 'Send Reset Instructions'}
                  </button>
                </form>

                <p style={styles.modalFooter}>
                  Remember your password?{' '}
                  <button
                    type="button"
                    style={styles.forgotPasswordLink}
                    onClick={() => setShowForgotPassword(false)}
                  >
                    Sign In
                  </button>
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Account Selector Modal */}
      {showAccountSelector && (
        <div style={styles.modalOverlay} onClick={() => setShowAccountSelector(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button
              style={styles.modalClose}
              onClick={() => setShowAccountSelector(false)}
              aria-label="Close"
            >
              &times;
            </button>

            <h2 style={styles.modalTitle}>Choose Your Account</h2>
            <p style={styles.modalText}>
              Multiple accounts found for <strong>{savedCredentials?.email}</strong>. Which would you like to access?
            </p>

            <div style={styles.accountList}>
              {availableAccounts.map((account, index) => (
                <button
                  key={index}
                  style={styles.accountOption}
                  onClick={() => handleAccountSelect(account)}
                  disabled={loading}
                >
                  <span style={styles.accountIcon}>
                    {account.icon || (account.type === 'bridge_club' ? '🌾' : account.type === 'portal' ? '🏪' : '👤')}
                  </span>
                  <div style={styles.accountInfo}>
                    <div style={styles.accountLabel}>{account.label}</div>
                    <div style={styles.accountSubtitle}>{account.subtitle}</div>
                  </div>
                  <span style={styles.accountArrow}>→</span>
                </button>
              ))}
            </div>

            {loading && (
              <p style={{ textAlign: 'center', color: '#666', marginTop: '1rem' }}>
                Signing in...
              </p>
            )}

            {error && <div style={{ ...styles.error, marginTop: '1rem' }}>{error}</div>}
          </div>
        </div>
      )}
      <InstallAppBanner />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a4d1a 0%, #2e7d32 50%, #1b5e20 100%)',
    padding: '2rem',
  },
  loginCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '420px',
    transition: 'max-width 0.3s ease',
  },
  header: {
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  logo: {
    height: '70px',
    marginBottom: '0.75rem',
  },
  title: {
    margin: 0,
    fontSize: '1.6rem',
    fontWeight: 700,
    color: '#722F37',
    textTransform: 'uppercase',
    letterSpacing: '2px',
  },
  subtitle: {
    margin: '0.5rem 0 0',
    fontSize: '0.9rem',
    color: '#666',
  },
  tagline: {
    margin: '0.5rem 0 0',
    fontSize: '0.7rem',
    color: '#1a4d1a',
    fontWeight: 600,
    letterSpacing: '1.5px',
  },
  tabContainer: {
    display: 'flex',
    marginBottom: '1.5rem',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '2px solid #e0e0e0',
  },
  tab: {
    flex: 1,
    padding: '0.75rem',
    border: 'none',
    background: '#f5f5f5',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#666',
    transition: 'all 0.2s ease',
  },
  activeTab: {
    background: '#722F37',
    color: '#ffffff',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '0.75rem',
  },
  error: {
    backgroundColor: '#ffebee',
    border: '1px solid #ef5350',
    color: '#c62828',
    padding: '0.75rem 1rem',
    borderRadius: '6px',
    fontSize: '0.9rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#333',
  },
  input: {
    padding: '0.75rem 0.875rem',
    fontSize: '16px', // Prevents iOS auto-zoom
    border: '2px solid #e0e0e0',
    borderRadius: '6px',
    outline: 'none',
    backgroundColor: '#E8F0F8',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    minHeight: '44px', // Touch target
  },
  select: {
    padding: '0.75rem 0.875rem',
    fontSize: '16px', // Prevents iOS auto-zoom
    border: '2px solid #e0e0e0',
    borderRadius: '6px',
    outline: 'none',
    backgroundColor: '#E8F0F8',
    cursor: 'pointer',
    minHeight: '44px', // Touch target
  },
  submitButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    border: 'none',
    padding: '0.875rem',
    fontSize: '1rem',
    fontWeight: 600,
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '0.5rem',
    transition: 'transform 0.2s, box-shadow 0.2s',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  termsText: {
    fontSize: '0.75rem',
    color: '#888',
    textAlign: 'center',
    margin: '0.5rem 0 0',
  },
  forgotPassword: {
    marginTop: '1.25rem',
    textAlign: 'center',
    paddingTop: '1rem',
    borderTop: '1px solid #e0e0e0',
    fontSize: '0.9rem',
    color: '#666',
  },
  footer: {
    marginTop: '1.25rem',
    textAlign: 'center',
    borderTop: '1px solid #e0e0e0',
    paddingTop: '1.25rem',
  },
  motto: {
    margin: '0 0 0.75rem',
    fontSize: '0.85rem',
    fontStyle: 'italic',
    color: '#1a4d1a',
  },
  contactInfo: {
    marginTop: '0.5rem',
  },
  contactText: {
    fontSize: '0.8rem',
    color: '#666',
    margin: 0,
    lineHeight: 1.6,
  },
  contactLink: {
    color: '#722F37',
    textDecoration: 'none',
    fontWeight: 500,
  },
  successMessage: {
    textAlign: 'center',
    padding: '1.5rem 0',
  },
  successIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#28a745',
    color: '#fff',
    fontSize: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1rem',
  },
  successTitle: {
    color: '#28a745',
    fontSize: '1.5rem',
    margin: '0 0 1rem',
  },
  successText: {
    color: '#333',
    fontSize: '1rem',
    margin: '0 0 0.5rem',
    lineHeight: 1.6,
  },
  successSubtext: {
    color: '#666',
    fontSize: '0.9rem',
    margin: '0 0 1.5rem',
  },
  backButton: {
    background: '#722F37',
    color: '#ffffff',
    border: 'none',
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    fontWeight: 600,
    borderRadius: '8px',
    cursor: 'pointer',
  },
  // Email check styles
  inputError: {
    borderColor: '#ef5350',
    backgroundColor: '#ffebee',
  },
  inputValid: {
    borderColor: '#4caf50',
  },
  emailCheckLoading: {
    fontSize: '0.8rem',
    color: '#666',
    fontStyle: 'italic',
  },
  emailCheckError: {
    fontSize: '0.8rem',
    color: '#c62828',
    marginTop: '0.25rem',
  },
  emailCheckValid: {
    fontSize: '0.8rem',
    color: '#2e7d32',
    marginTop: '0.25rem',
  },
  // Forgot password styles
  forgotPasswordLink: {
    background: 'none',
    border: 'none',
    color: '#722F37',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: 'inherit',
    fontWeight: 500,
    padding: 0,
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '2rem',
    maxWidth: '420px',
    width: '100%',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  modalClose: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#666',
    padding: '0.25rem 0.5rem',
    lineHeight: 1,
  },
  modalTitle: {
    margin: '0 0 0.75rem',
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#333',
  },
  modalText: {
    margin: '0 0 1.25rem',
    fontSize: '0.95rem',
    color: '#666',
    lineHeight: 1.5,
  },
  modalSubtext: {
    margin: '0 0 1.25rem',
    fontSize: '0.85rem',
    color: '#888',
  },
  modalButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    border: 'none',
    padding: '0.875rem',
    fontSize: '1rem',
    fontWeight: 600,
    borderRadius: '8px',
    cursor: 'pointer',
    width: '100%',
    marginTop: '0.5rem',
  },
  modalFooter: {
    marginTop: '1.25rem',
    textAlign: 'center',
    fontSize: '0.9rem',
    color: '#666',
  },
  modalSuccess: {
    textAlign: 'center',
  },
  bridgeClubSwitch: {
    marginTop: '0.75rem',
    textAlign: 'center',
    fontSize: '0.875rem',
    color: '#666',
  },
  bridgeClubLink: {
    color: '#E73C3E',
    textDecoration: 'none',
    fontWeight: 600,
  },
  // Account selector styles
  accountList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  accountOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    background: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left',
    width: '100%',
  },
  accountIcon: {
    fontSize: '2rem',
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f5f5f5',
    borderRadius: '12px',
  },
  accountInfo: {
    flex: 1,
  },
  accountLabel: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: '0.25rem',
  },
  accountSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
  },
  accountArrow: {
    fontSize: '1.25rem',
    color: '#999',
  },
};

export default PortalLogin;
